import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import { MetaData, SortedDocumentResponse, Page, PageUpdate } from '@/api';
import { PAGE_SCALE_STEP, PAGE_SCALE_MIN_VALUE, PAGE_SCALE_MAX_VALUE } from '@/constants';

interface DocumentState {
  metadata: MetaData | null;
  pages: Page[] | null;
  pageIdToIndexMap: Record<string, number>; // Map pageId to array
  pageScale: number;
  currentPageIndex: number;
}

const initialState: DocumentState = {
  metadata: null,
  pages: null,
  pageIdToIndexMap: {},
  pageScale: 1,
  currentPageIndex: 0
};

const gdprDocumentSlice = createSlice({
  name: 'gdprDocument',
  initialState,
  reducers: {
    setDocument(state, action: PayloadAction<SortedDocumentResponse>) {
      state.metadata = action.payload.metadata;
      // Store pages as sorted array directly
      state.pages = action.payload.pages;

      // Build pageId to index map for efficient local updates
      state.pageIdToIndexMap = {};
      action.payload.pages.forEach((page, index) => {
        state.pageIdToIndexMap[page.id] = index;
      });

      // Reset navigation when document changes
      state.currentPageIndex = 0;
    },
    clearDocument(state) {
      state.metadata = null;
      state.pages = null;
      state.pageIdToIndexMap = {};
      state.pageScale = 1;
      state.currentPageIndex = 0;
    },
    setPageScaleZoomIn: (state) => {
      const newValue = Math.min(state.pageScale + PAGE_SCALE_STEP, PAGE_SCALE_MAX_VALUE);
      state.pageScale = newValue;
    },
    setPageScaleZoomOut: (state) => {
      const newValue = Math.max(state.pageScale - PAGE_SCALE_STEP, PAGE_SCALE_MIN_VALUE);
      state.pageScale = newValue;
    },
    setCurrentPageIndex: (state, action: PayloadAction<number>) => {
      if (state.pages) {
        const newIndex = Math.max(0, Math.min(action.payload, state.pages.length - 1));
        state.currentPageIndex = newIndex;
      }
    },
    updatePagesLocally: (state, action: PayloadAction<PageUpdate[]>) => {
      if (!state.pages || !action.payload.length) {
        return;
      }

      // Update each page using the pageId-to-index map for O(1) lookup
      action.payload.forEach((pageUpdate) => {
        const pageIndex = state.pageIdToIndexMap[pageUpdate.pageId];

        if (pageIndex !== undefined && state.pages && state.pages[pageIndex]) {
          // Update only the label fields
          state.pages[pageIndex].confirmedBlank = pageUpdate.confirmedBlank;
          state.pages[pageIndex].confirmedBreach = pageUpdate.confirmedBreach;
          state.pages[pageIndex].detectedBlank = pageUpdate.detectedBlank;
          state.pages[pageIndex].detectedBreach = pageUpdate.detectedBreach;
        }
      });
    }
  }
});

export const {
  setDocument,
  clearDocument,
  setPageScaleZoomIn,
  setPageScaleZoomOut,
  setCurrentPageIndex,
  updatePagesLocally
} = gdprDocumentSlice.actions;
export const selectPageData = (state: RootState) => state.gdprDocument.pages;
export const selectMetadata = (state: RootState) => state.gdprDocument.metadata;
export const selectPageScale = (state: RootState) => state.gdprDocument.pageScale;
export const selectCurrentPageIndex = (state: RootState) => state.gdprDocument.currentPageIndex;
export const gdprDocumentReducer = gdprDocumentSlice.reducer;
